import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios, { AxiosError } from 'axios';
import { getApiHost } from '@/appSettings';
import { useNotificationStore } from '@/store/notificationStore';
import { NotificationStates } from '@/models/enums/NotificationStates';
import { Guid } from 'guid-typescript';
import { i18n } from '@/i18n';
import { useLoginStore } from '@/login/stores/useLoginStore';

type RequestData = {
    instance: AxiosInstance,
    url: string,
    postData: object
};

type Header = { [key: string]: string; };

export class RestManager {
    /** Address to the service we want to call */
    private readonly serviceAdress: string;

    private controller = new AbortController();

    public constructor(
        serviceAdress: string
    ) {
        if (!serviceAdress.startsWith('http')) {
            // If the serviceAdress does not start with http, we expect it to be an api service and wrap it in the api service address.
            serviceAdress = `https://${getApiHost()}/${serviceAdress}`;
        }
        this.serviceAdress = serviceAdress;
    }

    /**
     * Sends a POST to the endpoint function, and returns its data.
     * @param data The data (if any) to request.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    public async post<T>(
        data?: unknown,
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        contentType?: string,
        local?: string
    ): Promise<T> {
        const request = this.prepareRequest(data, method, customHeaders, options, contentType, local);
        try {
            const result = await request.instance.post<T>(request.url, request.postData);
            return result.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                console.error('USER NOT AUTHENTICATED', request.url);
                if (!useLoginStore().isSimulating) {
                    useNotificationStore().add({
                        id: Guid.create(),
                        state: NotificationStates.Warning,
                        message: i18n.global.t('system.notAllowed'),
                        created: new Date(),
                        isPreviewHidden: false
                    });
                }
            }

            throw (error);
        }
    }

    /**
     * Sends a GET to the endpoint function, and returns its data.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    public async get<T>(
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        local?: string
    ): Promise<T> {
        const request = this.prepareRequest(null, method, customHeaders, options, undefined, local);

        try {
            const result = await request.instance.get<T>(request.url);
            return result.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                console.error('USER NOT AUTHENTICATED', request.url);
                if (!useLoginStore().isSimulating) {
                    useNotificationStore().add({
                        id: Guid.create(),
                        state: NotificationStates.Warning,
                        message: i18n.global.t('system.notAllowed'),
                        created: new Date(),
                        isPreviewHidden: false
                    });
                }
            }

            throw (error);
        }
    }

    /**
     * Sends a PUT to the endpoint function, and returns its data.
     * @param data The data (if any) to request.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    public async put<T>(
        data?: unknown,
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        local?: string
    ): Promise<T> {
        const request = this.prepareRequest(data, method, customHeaders, options, undefined, local);

        try {
            const result = await request.instance.put<T>(request.url, request.postData);
            return result.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                console.error('USER NOT AUTHENTICATED', request.url);
                if (!useLoginStore().isSimulating) {
                    useNotificationStore().add({
                        id: Guid.create(),
                        state: NotificationStates.Warning,
                        message: i18n.global.t('system.notAllowed'),
                        created: new Date(),
                        isPreviewHidden: false
                    });
                }
            }

            throw (error);
        }
    }

    /**
     * Sends a DELETE to the endpoint function, and returns its data.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    public async delete<T>(
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        local?: string
    ): Promise<T> {
        const request = this.prepareRequest(null, method, customHeaders, options, undefined, local);

        try {
            const result = await request.instance.delete<T>(request.url, options);
            return result.data;
        } catch (error: unknown) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                console.error('USER NOT AUTHENTICATED', request.url);
                if (!useLoginStore().isSimulating) {
                    useNotificationStore().add({
                        id: Guid.create(),
                        state: NotificationStates.Warning,
                        message: i18n.global.t('system.notAllowed'),
                        created: new Date(),
                        isPreviewHidden: false
                    });
                }
            }

            throw (error);
        }
    }

    /**
     * Creates a request object to send axios.
     * @param data The data (if any) to send to the method.
     * @param method Method to run if the url is an api with multiple methods.
     * @param customHeaders Object containing any header values that are specific to this call.
     */
    private prepareRequest(
        data?: unknown,
        method?: string,
        customHeaders?: Header,
        options?: AxiosRequestConfig,
        contentType?: string,
        local?: string
    ): RequestData {
        // Get the url to the API from the settings
        let url: string = this.serviceAdress;

        if (local != null) {
            url = local;
        }

        if (method != null) {
            url += `/${method}`;
        }

        // If the data is null or undefined, use empty object
        const postData = data ?? {};

        // Create an instance of Axios
        const instance = axios.create({...options, withCredentials: true });

        instance.interceptors.request.use(request => {
            request.headers.set('Content-Type', contentType ?? 'application/json');

            if (customHeaders != null) {
                for (const key of Object.keys(customHeaders)) {
                    request.headers.set(key, customHeaders[key]);
                }
            }

            if (this.controller != null && request.signal == null) {
                request.signal = this.controller.signal;
            }

            return request;
        });

        // HTTP spans are produced by @opentelemetry/instrumentation-xml-http-request
        // (registered in useOpentelemetry.ts) — no manual interceptor needed.

        // Return the values
        return {
            instance,
            url,
            postData
        };
    }

    public abort(): void {
        this.controller.abort();
        this.resetAbort();
    }

    public resetAbort(): void {
        this.controller = new AbortController();
    }
}
